import { Columns3, SlidersHorizontal, Upload } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DEFAULT_VISIBLE_COLUMNS, EXTRA_COLUMNS, COLUMN_LABELS } from '../documents.constants'
import { DocumentColumnKey, TagFacet, DOCUMENT_CATEGORIES } from '../documents.types'
import { TagFacetSelector } from './tag-facet-selector'

type DocumentsToolbarProps = {
  search: string
  onSearchChange: (search: string) => void
  category: string
  onCategoryChange: (category: string) => void
  tags: TagFacet[]
  selectedTags: string[]
  onTagsChange: (tags: string[]) => void
  tagsLoading?: boolean
  visibleColumns: DocumentColumnKey[]
  onVisibleColumnsChange: (columns: DocumentColumnKey[]) => void
  onOpenFilters: () => void
  onUpload: () => void
}

export const DocumentsToolbar = ({
  search,
  onSearchChange,
  category,
  onCategoryChange,
  tags,
  selectedTags,
  onTagsChange,
  tagsLoading,
  visibleColumns,
  onVisibleColumnsChange,
  onOpenFilters,
  onUpload,
}: DocumentsToolbarProps) => {
  const toggleColumn = (column: DocumentColumnKey, checked: boolean) => {
    onVisibleColumnsChange(
      checked ? [...visibleColumns, column] : visibleColumns.filter((c) => c !== column),
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2 justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={category} onValueChange={onCategoryChange}>
          <SelectTrigger className="h-10 w-[180px] rounded-lg bg-background text-sm shadow-sm border border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {DOCUMENT_CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <TagFacetSelector
          tags={tags}
          selected={selectedTags}
          onChange={onTagsChange}
          isLoading={tagsLoading}
        />

        <Input
          placeholder="Search documents"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-10 w-[260px] rounded-lg bg-background text-sm shadow-sm border border-border"
        />
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Columns3 className="h-4 w-4" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Show columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {[...DEFAULT_VISIBLE_COLUMNS, ...EXTRA_COLUMNS].map((column) => (
              <DropdownMenuCheckboxItem
                key={column}
                checked={visibleColumns.includes(column)}
                onCheckedChange={(checked) => toggleColumn(column, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {COLUMN_LABELS[column]}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="outline" onClick={onOpenFilters}>
          <SlidersHorizontal className="h-4 w-4" />
          Filters
        </Button>

        <Button onClick={onUpload}>
          <Upload className="h-4 w-4" />
          Upload
        </Button>
      </div>
    </div>
  )
}
